import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { find, findIndex, maxBy, pick, sortBy } from 'lodash';
import { ArrowBackIcon } from '../icons/ArrowBackIcon';
import { CheckIcon } from '../icons/CheckIcon';
import { Flex } from './Flex';
import { Paragraph } from './Text';
import { contextSearch, snakeCase } from '../utils';
import { Input } from './CustomInput';
import { SearchIcon } from '../icons/SearchIcon';


const StyledSelectButton = styled.div`
    height: 40px;
    background: ${({selected}) => selected ? "#D2E1FC" : "#E4EDFD"};
    border-radius: 8px;
    padding: 0 12px 0 12px;
    display: flex;
    align-items: center;
    color: ${({selected}) => selected ? "var(--sp-paragraph-color)" : "var(--sp-label-color)"};
    font-weight: 600;

    &:hover {
        background: ${({selected}) => selected ? "#BAD1FA" : "#D2E1FC"};
        cursor: pointer;
    }

    & > span:not(:last-child) {
        margin-right: 8px;
        white-space: nowrap;
    }
`

const StyledSelectButtonBadge = styled.span`
    font-size: 12px;
    color: #FFFFFF;
    background: #8898B2;
    padding: 0 4px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledListWrapper = styled(Flex).attrs({

})`
    display: ${({ visible }) => visible ? "block" : "none" };
    width: ${({ width }) => width};
    position: absolute;
    top: 44px;
    right: 0;
    padding: 0;
    box-shadow: 0px 4px 6px rgba(116, 151, 208, 0.2);
    z-index: 100;
    background: #FFFFFF;
    border-radius: 8px;
    margin-top: 6px;
`;

const StyledList = styled.ul`
    list-style-type: none;
    padding: 0;
    max-height: 252px;
    margin-top: 1em;
    overflow-y: scroll;
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(139, 164, 206, 0.4);
        border-radius: 10px;
        border: 1px solid transparent;
        background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(139, 164, 206, 0.6);
      border: 1px solid transparent;
      background-clip: padding-box;
    }
`;

const StyledListItem = styled.li`
    padding: 6px 16px 6px 24px;
    display: flex;
    height: 36px;
    align-items: center;
    justify-content: space-between;

    &:hover {
        background: #F6F9FF;
        border-left: 2px solid #E4EDFD;
        padding-left: 22px;
    }
`;

const SelectButton = ({ options=[], filterKey, multiple, onSearch, selectedOptions=null, ...props }) => {
    const initialState = {
        visible: false,
        options: null,
        cache: null
    };
    const [ state, setState ] = useState(initialState);
    const wrapperRef = useRef();

    useEffect(() => {
        document.addEventListener("click", handleClickOutside, false);
        return () => {
            document.removeEventListener("click", handleClickOutside, false);
        };
    });

    const handleClick = (e) => {
        e.preventDefault();
        if (state.visible) {
            setState(initialState);
        } else {
            setState(state => ({ ...state, visible: true, options: sortBy(options, option => findIndex(selectedOptions, o => snakeCase(o) == snakeCase(option.key)) < 0), cache: options.map(o => pick(o, ["key", "title"])) }));
        }
    };

    const handleClickOutside = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            if (state.visible) setState({ visible: false });
        };
    };

    const getListWidth = (title) => {
        let titleWidth = 0, offset = 100;
        let canvas = document.createElement("canvas"),
        context = canvas.getContext("2d");
        context.font = "400 14px sans-serif";
        titleWidth = Math.ceil(context.measureText(title).width);
        return titleWidth + offset + "px";
    };

    const onOptionSelect = (value) => {
        let selected;
        if (multiple) {
            selected = selectedOptions ? selectedOptions.slice() : [];
            let idx = findIndex(selected, o => snakeCase(o) === snakeCase(value));
            if (idx < 0) {
                selected.push(value);
            } else {
                delete selected.splice(idx, 1)
            }
        } else {
            selected = value == selectedOptions ? null : value;
            if (state.visible) setState({ visible: false });
        };
        onSearch(selected);
    };

    const getOptionTitle = (key) => {
        let option = find(options, o => snakeCase(o.key) == snakeCase(key));
        if (option && option.hasOwnProperty("title")) {
            return option.title
        };
        return key;
    };

    const getButtonCaption = (text) => {
        let caption = text, selected = Array.isArray(selectedOptions) ? selectedOptions[0] : selectedOptions;
        if (selected !== undefined && selected !== null) {
            caption = <span>{caption}:&nbsp;{getOptionTitle(selected)}</span>;
            if (Array.isArray(selectedOptions) && selectedOptions.length > 1) {
            caption = <>
                    {caption}
                    <StyledSelectButtonBadge>+{selectedOptions.length - 1}</StyledSelectButtonBadge>
                </>;
            }
            return caption;
        };

        return <span>{caption}</span>;
    };


    return(
        <Flex position="relative" ref={wrapperRef}>
            <StyledSelectButton onClick={handleClick} selected={Array.isArray(selectedOptions) ? selectedOptions.length : selectedOptions !== null} {...props}>
                {getButtonCaption(props.children)}
                <ArrowBackIcon rotate={270} color="var(--sp-label-color)" />
            </StyledSelectButton>
            {Array.isArray(state.cache)
                ? <StyledListWrapper visible={state.visible} width={getListWidth(maxBy(options, o => o.title.length).title)}>
                    {state.cache.length > 7 &&
                    <Flex align="center" padding={{ left: "24px", top: "12px", right: "12px" }} margin={{ bottom: "1em" }} style={{ borderBottom: "solid 1px #E4EDFD"}}>
                        <Input autoFocus bordered={false} placeholder="Search..." onChange={e => setState(state => ({ ...state, options: contextSearch(state.cache, e.target.value) }))} style={{ marginRight: "6px"}} />
                        <SearchIcon size={16} color="var(--sp-label-color)" />
                    </Flex>}
                    <StyledList>
                        {state.options.map(option => (
                            <StyledListItem key={option.key} onClick={() => onOptionSelect(option.key)}>
                                <Paragraph nowrap>{option.title}</Paragraph>
                                {option.key == selectedOptions || findIndex(selectedOptions, o => snakeCase(o) == snakeCase(option.key)) > -1 ? <CheckIcon size={14} color="var(--sp-main-color-hover)" /> : null}
                            </StyledListItem>
                        ))}
                    </StyledList>
                </StyledListWrapper>
                : null}
        </Flex>)
};

export { SelectButton };